import React, { useState, useEffect } from 'react';
import {
    FaLightbulb,
    FaSpinner,
    FaExclamationTriangle,
    FaRedo,
    FaCheck,
    FaClock,
    FaInstagram,
    FaWhatsapp,
    FaGift,
    FaChartLine,
    FaEye,
    FaEyeSlash
} from 'react-icons/fa';
import { get, patch } from '../../helpers/apiHelper';
import useAuthStore from '../../store/authStore';
import Swal from 'sweetalert2';

const RecommendedActionsCard = () => {
    const { user } = useAuthStore();
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [generatingAnalysis, setGeneratingAnalysis] = useState(false);
    const [updatingAction, setUpdatingAction] = useState(null);
    const [showDetails, setShowDetails] = useState({});
    const [updatedActions, setUpdatedActions] = useState(new Set());

    // Generar análisis en tiempo real
    const generateNewAnalysis = async () => {
        try {
            setGeneratingAnalysis(true);
            setError(null);

            const response = await get(`weekly-analysis/recommendations/${user.userId}`);

            if (response && response.recommendedActions && response.recommendedActions.length > 0) {
                setAnalysis(response);
                // Esperar un momento antes de recargar para asegurar que todo se guardó
                setTimeout(async () => {
                    try {
                        const updatedResponse = await get(`weekly-analysis/saved/${user.userId}`);
                        if (updatedResponse && updatedResponse.recommendedActions) {
                            setAnalysis(updatedResponse);
                        }
                    } catch (err) {
                        console.error('Error fetching updated analysis:', err);
                    }
                }, 1000);
            } else {
                setAnalysis(null);
                setError('No se pudieron generar recomendaciones');
            }
        } catch (err) {
            console.error('Error generating new analysis:', err);
            setError('No se pudo generar el análisis semanal');
            setAnalysis(null);
        } finally {
            setGeneratingAnalysis(false);
        }
    };

    // Efecto para recargar periódicamente mientras se genera
    useEffect(() => {
        let interval;
        if (generatingAnalysis) {
            interval = setInterval(async () => {
                try {
                    const response = await get(`weekly-analysis/saved/${user.userId}`);
                    if (response && response.recommendedActions && response.recommendedActions.length > 0) {
                        setAnalysis(response);
                        setGeneratingAnalysis(false);
                    }
                } catch (err) {
                    console.error('Error checking analysis status:', err);
                }
            }, 2000); // Comprobar cada 2 segundos
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [generatingAnalysis, user.userId]);

    // Obtener el análisis guardado
    const fetchSavedAnalysis = async () => {
        if (generatingAnalysis) return; // Evitar llamadas mientras se está generando

        try {
            setLoading(true);
            setError(null);

            const response = await get(`weekly-analysis/saved/${user.userId}`);

            if (response && response.recommendedActions && response.recommendedActions.length > 0) {
                setAnalysis(response);
            } else {
                // Si no hay análisis, generar uno nuevo
                await generateNewAnalysis();
            }
        } catch (err) {
            console.error('Error fetching saved analysis:', err);
            // Si es 404 o cualquier otro error, intentar generar nuevo análisis
            await generateNewAnalysis();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.userId) {
            fetchSavedAnalysis();
        }
    }, [user]);

    // Actualizar estado de una recomendación
    const updateActionStatus = async (actionId, status, notes = '') => {
        try {
            setUpdatingAction(actionId);

            const response = await patch(`weekly-analysis/recommendation/${user.userId}/${actionId}`, {
                status,
                notes
            });


            // Actualizar el estado local inmediatamente
            setAnalysis(prev => {
                if (!prev || !prev.recommendedActions) return prev;

                const updatedActions = prev.recommendedActions.map(action => {
                    if (action.id === actionId) {
                        return {
                            ...action,
                            status: status,
                            notes: notes,
                            completedAt: status === 'completed' ? new Date().toISOString() : action.completedAt
                        };
                    }
                    return action;
                });

                return {
                    ...prev,
                    recommendedActions: updatedActions
                };
            });

            // Si la respuesta del backend es exitosa, usar esos datos directamente
            if (response && response.recommendedActions) {
                setAnalysis(response);
            }


            // Marcar la acción como actualizada
            setUpdatedActions(prev => new Set([...prev, actionId]));

            // Limpiar el indicador de actualización después de un tiempo
            setTimeout(() => {
                setUpdatedActions(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(actionId);
                    return newSet;
                });
            }, 3000);

        } catch (err) {
            console.error('Error updating action status:', err);
            console.error('Detalles del error:', {
                message: err.message,
                stack: err.stack,
                userId: user.userId,
                actionId,
                status,
                notes
            });
            alert('Error al actualizar el estado de la tarea');
        } finally {
            setUpdatingAction(null);
        }
    };



    const getHealthColor = (status) => {
        switch (status) {
            case 'excellent':
                return 'bg-green-100 text-green-800';
            case 'good':
                return 'bg-blue-100 text-blue-800';
            case 'stable':
                return 'bg-yellow-100 text-yellow-800';
            case 'concerning':
            case 'critical':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'in_progress':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'rescheduled':
                return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'pending':
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getActionIcon = (type) => {
        switch (type) {
            case 'instagram_post':
                return <FaInstagram className="text-pink-500" />;
            case 'whatsapp_campaign':
                return <FaWhatsapp className="text-green-500" />;
            case 'promotion':
                return <FaGift className="text-red-500" />;
            default:
                return <FaLightbulb className="text-blue-500" />;
        }
    };

    const getDayName = (day) => {
        const days = {
            monday: 'Lunes',
            tuesday: 'Martes',
            wednesday: 'Miércoles',
            thursday: 'Jueves',
            friday: 'Viernes',
            weekend: 'Fin de semana'
        };
        return days[day] || day;
    };

    const getTimeName = (time) => {
        const times = {
            morning: 'Mañana',
            afternoon: 'Tarde',
            evening: 'Noche'
        };
        return times[time] || time;
    };

    if (loading || generatingAnalysis) {
        return (
            <div className="recommendations-list">
                <div className="flex items-center justify-center p-8">
                    <FaSpinner className="animate-spin text-blue-500 text-2xl mr-3" />
                    <span className="text-gray-600">
                        {generatingAnalysis ? 'Creando tu estrategia personalizada...' : 'Cargando tu estrategia...'}
                    </span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="recommendations-list">
                <div className="text-center p-6">
                    <FaSpinner className="text-blue-500 text-3xl mx-auto mb-3 animate-spin" />
                    <p className="text-gray-600 mb-4">Preparando tu estrategia personalizada...</p>
                    <button
                        onClick={generateNewAnalysis}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                        Intentar de nuevo
                    </button>
                </div>
            </div>
        );
    }

    // Solo mostrar estado vacío si no hay análisis
    if (!analysis || !analysis.recommendedActions || analysis.recommendedActions.length === 0) {
        return (
            <div className="recommendations-list">
                <div className="text-center p-6">
                    <FaLightbulb className="text-blue-500 text-3xl mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        Estrategia Semanal Personalizada
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                        PetitPrime está analizando tu negocio para crear la mejor estrategia. En breve tendrás tus acciones personalizadas.
                    </p>
                    <button
                        onClick={generateNewAnalysis}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                        Generar Estrategia
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="recommendations-list p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3 w-full sm:w-auto mb-2 sm:mb-0">
                    <FaChartLine className="text-blue-500 flex-shrink-0" />
                    <div className="min-w-0">
                        <span className="text-xs sm:text-sm text-gray-600 block">
                            Análisis del {new Date(analysis.analysisDate).toLocaleDateString('es-ES')}
                        </span>
                        {analysis.primaryObjective && (
                            <div className="text-xs text-gray-500 mt-1 truncate max-w-[200px] sm:max-w-none">
                                Objetivo: {analysis.primaryObjective.description}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex items-center space-x-2 w-full sm:w-auto justify-end">
                    {analysis.businessHealth?.overall && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getHealthColor(analysis.businessHealth.overall)}`}>
                            {analysis.businessHealth.overall.toUpperCase()}
                        </span>
                    )}
                    <button
                        onClick={() => {
                            fetchSavedAnalysis();
                        }}
                        className="p-2 sm:px-2 sm:py-1 text-xs bg-gray-200 text-gray-600 rounded hover:bg-gray-300"
                        title="Recargar datos"
                    >
                        🔄
                    </button>
                </div>
            </div>

            {/* Lista de recomendaciones con interactividad completa */}
            {analysis.recommendedActions.map((action) => (
                <div
                    key={action.id}
                    className={`recommendation-item bg-white border rounded-lg mb-3 p-2 sm:p-4 transition-all duration-300 hover:shadow-lg group ${updatedActions.has(action.id)
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-200'
                        }`}
                >
                    {/* Header de la tarea */}
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-3">
                        <div className="flex items-start space-x-3 flex-1 mb-2 sm:mb-0">
                            <div className="flex-shrink-0 mt-1">
                                {getActionIcon(action.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-semibold text-gray-900 mb-1 break-words">
                                    {action.title}
                                </h4>
                                <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                                    <span className={`px-2 py-1 rounded-full ${getStatusColor(action.status)}`}>
                                        {action.status === 'completed' ? 'Completado' :
                                            action.status === 'in_progress' ? 'En progreso' :
                                                action.status === 'rescheduled' ? 'Reprogramado' : 'Pendiente'}
                                    </span>
                                    {action.recommendedDay && action.recommendedTime && (
                                        <span className="flex items-center space-x-1">
                                            <FaClock className="text-gray-400" />
                                            <span className="break-words">{getDayName(action.recommendedDay)} - {getTimeName(action.recommendedTime)}</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Botones de acción */}
                        <div className="flex justify-end">
                            <button
                                onClick={() => setShowDetails(prev => ({ ...prev, [action.id]: !prev[action.id] }))}
                                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                title="Ver detalles"
                            >
                                {showDetails[action.id] ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>

                    {/* Descripción expandible */}
                    {showDetails[action.id] && (
                        <div className="mb-4 p-2 sm:p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-700 mb-3 break-words">{action.description}</p>

                            {/* Contenido sugerido */}
                            {action.suggestedContent && (
                                <div className="mb-3">
                                    <h5 className="text-xs font-semibold text-gray-600 mb-1">Contenido sugerido:</h5>
                                    <div className="text-sm text-gray-700 bg-white p-2 rounded border break-words">
                                        {action.suggestedContent}
                                    </div>
                                </div>
                            )}

                            {/* Audiencia objetivo */}
                            {action.targetAudience && (
                                <div className="mb-3">
                                    <h5 className="text-xs font-semibold text-gray-600 mb-1">Audiencia objetivo:</h5>
                                    <span className="text-sm text-gray-700 break-words">{action.targetAudience}</span>
                                </div>
                            )}

                            {/* Call to Action */}
                            {action.callToAction && (
                                <div className="mb-3">
                                    <h5 className="text-xs font-semibold text-gray-600 mb-1">Call to Action:</h5>
                                    <span className="text-sm text-gray-700 font-medium break-words">{action.callToAction}</span>
                                </div>
                            )}

                            {/* Notas del usuario */}
                            {action.notes && (
                                <div className="mb-3">
                                    <h5 className="text-xs font-semibold text-gray-600 mb-1">Notas:</h5>
                                    <div className="text-sm text-gray-700 bg-yellow-50 p-2 rounded border break-words">
                                        {action.notes}
                                    </div>
                                </div>
                            )}

                            {/* Fecha de completado */}
                            {action.completedAt && (
                                <div className="text-xs text-gray-500">
                                    Completado el {new Date(action.completedAt).toLocaleDateString('es-ES')}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Barra de acciones */}
                    <div className="flex flex-wrap gap-2 items-center justify-start pt-3 border-t border-gray-100">
                        {/* Botón Completar */}
                        {action.status !== 'completed' && (
                            <button
                                onClick={() => updateActionStatus(action.id, 'completed')}
                                disabled={updatingAction === action.id}
                                className="flex items-center space-x-1 px-4 py-2 sm:px-3 sm:py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:opacity-50 transition-colors touch-manipulation"
                            >
                                {updatingAction === action.id ? (
                                    <FaSpinner className="animate-spin" />
                                ) : (
                                    <FaCheck />
                                )}
                                <span>Completar</span>
                            </button>
                        )}

                        {/* Botón En Progreso */}
                        {action.status === 'pending' && (
                            <button
                                onClick={() => updateActionStatus(action.id, 'in_progress')}
                                disabled={updatingAction === action.id}
                                className="flex items-center space-x-1 px-4 py-2 sm:px-3 sm:py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 disabled:opacity-50 transition-colors touch-manipulation"
                            >
                                {updatingAction === action.id ? (
                                    <FaSpinner className="animate-spin" />
                                ) : (
                                    <FaClock />
                                )}
                                <span>En Progreso</span>
                            </button>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};



export default RecommendedActionsCard; 